import { Box, IconButton, styled, useMediaQuery } from "@mui/material";
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import Scrollbar from "components/ScrollBar";
import { useState } from "react";
import ArrowLeftToLine from "icons/duotone/ArrowLeftToLine";
import MultiLevelMenu from "./MultiLevelMenu";
import MobileSidebar from "./MobileSidebar";

const TOP_HEADER_AREA = 70;

// Anchor to top + bottom of the viewport so the sidebar always fills
// the full visible height — robust against root-level `zoom` scaling
// and any future layout changes.
const SidebarWrapper = styled(Box)(({
  theme,
  compact
}) => ({
  position: "fixed",
  top: 0,
  bottom: 0,
  left: 0,
  width: compact ? 86 : 280,
  display: "flex",
  flexDirection: "column",
  transition: "width .2s ease",
  zIndex: theme.zIndex.drawer,
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  "&:hover": compact && {
    width: 280
  }
}));

const NavWrapper = styled(Box)(() => ({
  paddingLeft: 16,
  paddingRight: 16,
  paddingBottom: 16
}));

const LogoContainer = styled(FlexBox)(({ theme, compact }) => ({
  alignItems: "center",
  justifyContent: compact ? "center" : "flex-start",
  minWidth: compact ? "54px" : "auto",
  transition: "all 0.2s ease",
  overflow: "hidden"
}));

const LogoImage = styled("img")(({ theme, compact }) => ({
  height: compact ? "32px" : "40px",
  width: "auto",
  maxWidth: compact ? "32px" : "160px",
  objectFit: "contain",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "scale(1.02)"
  }
}));

const StyledArrow = styled(ArrowLeftToLine)(() => ({
  display: "block"
}));

const StyledIconButton = styled(IconButton)(({
  theme
}) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
}));

// -----------------------------------------------------------------------------
const DashboardSidebar = props => {
  const {
    sidebarCompact,
    showMobileSideBar,
    setShowMobileSideBar,
    setSidebarCompact
  } = props;
  const [onHover, setOnHover] = useState(false);
  const downLg = useMediaQuery(theme => theme.breakpoints.down("lg"));

  // Activate compact when toggle button clicked and not on hover state
  const COMPACT = sidebarCompact && !onHover ? 1 : 0;

  // IF MOBILE
  if (downLg) {
    return <MobileSidebar 
      sidebarCompact={!!COMPACT} 
      showMobileSideBar={!!showMobileSideBar} 
      setShowMobileSideBar={setShowMobileSideBar} 
    />;
  }

  return (
    <SidebarWrapper 
      compact={sidebarCompact ? 1 : 0} 
      onMouseEnter={() => setOnHover(true)} 
      onMouseLeave={() => sidebarCompact && setOnHover(false)}
    >
      <FlexBetween 
        pt={2} 
        pr={2} 
        pl={sidebarCompact && !onHover ? 2 : 3} 
        pb={1} 
        minHeight={TOP_HEADER_AREA}
        sx={{ flexShrink: 0 }}
      >
        {/* LOGO */}
        <LogoContainer compact={COMPACT}>
          <LogoImage 
            src="/nextgen/nextgen_logo.png"
            alt="NextGen Logo"
            compact={COMPACT}
          />
        </LogoContainer>

        {/* SIDEBAR COLLAPSE BUTTON */}
        {!COMPACT && (
          <StyledIconButton 
            onClick={setSidebarCompact}
            sx={{ ml: "auto" }}
          >
            <StyledArrow />
          </StyledIconButton>
        )}
      </FlexBetween>

      {/* NAVIGATION ITEMS — flex:1 fills the remaining vertical space */}
      <Scrollbar 
        autoHide 
        clickOnTrack={false} 
        sx={{
          flex: 1,
          minHeight: 0,
          overflowX: "hidden"
        }}
      >
        <NavWrapper>
          <MultiLevelMenu sidebarCompact={!!COMPACT} />
        </NavWrapper>
      </Scrollbar>
    </SidebarWrapper>
  );
};

export default DashboardSidebar;